import {
  accountIdFormat,
  inventoryItemIdFormat,
  locationIdFormat,
  processingSessionIdFormat,
  processingSessionItemIdFormat,
} from "@redotech/redo-model/ids";
import { ReturnsProcessingItemStatus } from "@redotech/redo-prisma-client/enums";
import { returnsProcessingSessionType } from "@redotech/redo-return-warehouse-processing-db/processing-types";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";

export {
  ReturnsProcessingSessionType,
  returnsProcessingSessionType,
} from "@redotech/redo-return-warehouse-processing-db/processing-types";

export const SessionStatus = ["active", "completed", "abandoned"] as const;
export type SessionStatus = (typeof SessionStatus)[number];

export const ItemProcessingStatusSchema = createEnum(
  ReturnsProcessingItemStatus,
);
export type ItemProcessingStatus = z.infer<typeof ItemProcessingStatusSchema>;

const ProcessingSessionItem_ = z.object({
  id: processingSessionItemIdFormat.schema,
  sessionId: processingSessionIdFormat.schema,
  returnItemId: z.string().nullable(),
  inventoryItemId: inventoryItemIdFormat.schema.nullable(),
  status: ItemProcessingStatusSchema,
  grade: z.string().nullable(),
  outcome: z.string().nullable(),
  notes: z.string().nullable(),
  productTitle: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
});
export const ProcessingSessionItem: z.ZodType<ProcessingSessionItem> =
  ProcessingSessionItem_;
export interface ProcessingSessionItem
  extends z.infer<typeof ProcessingSessionItem_> {}

export const ReturnsProcessingSessionTypeSchema = z.enum(
  returnsProcessingSessionType,
);

export const ProcessingSession_ = z.object({
  id: processingSessionIdFormat.schema,
  accountId: accountIdFormat.schema,
  returnId: z.string(),
  legacyUserId: z.string(),
  type: ReturnsProcessingSessionTypeSchema.nullable(),
  locationId: locationIdFormat.schema.nullable(),
  status: z.enum(SessionStatus),
  items: z.array(ProcessingSessionItem_),
  createdAt: z.date(),
  updatedAt: z.date(),
  completedAt: z.date().nullable(),
  deletedAt: z.date().nullable(),
});
export const ProcessingSession: z.ZodType<ProcessingSession> =
  ProcessingSession_;
export interface ProcessingSession extends z.infer<typeof ProcessingSession_> {}

export function deriveSessionStatus(session: {
  completedAt: Date | null;
  deletedAt: Date | null;
}): SessionStatus {
  if (session.deletedAt) {
    return "abandoned";
  }
  if (session.completedAt) {
    return "completed";
  }
  return "active";
}
